import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Heart, ArrowLeft, FileText } from "lucide-react";
import FileUpload from "@/components/FileUpload";

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-3 items-start">
      <Icon className="size-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-medium truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

export default function PatientProfilePage() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, t] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patients/${id}/timeline`),
      ]);
      setPatient(p.data);
      setTimeline(t.data);
    })();
  }, [id]);

  if (!patient) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5 fade-up" data-testid="patient-profile">
      <Link to="/app/patients" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
        <ArrowLeft className="size-4" /> Back to patients
      </Link>

      <Card>
        <CardContent className="p-6 flex flex-col md:flex-row gap-6 md:items-center">
          <Avatar className="size-20">
            <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
              {patient.name?.slice(0,2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold tracking-tight">{patient.name}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {patient.gender && <Badge variant="outline">{patient.gender}</Badge>}
              {patient.dob && <Badge variant="outline">DOB {patient.dob}</Badge>}
              {patient.blood_type && <Badge variant="outline" className="font-mono">{patient.blood_type}</Badge>}
              {patient.insurance_provider && <Badge variant="outline">{patient.insurance_provider}</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 min-w-[260px]">
            <InfoRow icon={Phone} label="Phone" value={patient.phone} />
            <InfoRow icon={Mail} label="Email" value={patient.email} />
            <InfoRow icon={MapPin} label="Address" value={patient.address} />
            <InfoRow icon={Heart} label="Emergency" value={patient.emergency_contact} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card>
          <CardHeader><CardTitle className="text-base">Allergies</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground whitespace-pre-line">{patient.allergies || "None reported"}</CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Conditions</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground whitespace-pre-line">{patient.conditions || "None reported"}</CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Notes</CardTitle></CardHeader>
          <CardContent className="text-sm text-muted-foreground whitespace-pre-line">{patient.notes || "—"}</CardContent>
        </Card>
      </div>

      <Tabs defaultValue="appointments">
        <TabsList>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments ({timeline?.appointments?.length || 0})</TabsTrigger>
          <TabsTrigger value="records">EMR ({timeline?.records?.length || 0})</TabsTrigger>
          <TabsTrigger value="prescriptions">Rx ({timeline?.prescriptions?.length || 0})</TabsTrigger>
          <TabsTrigger value="labs">Lab ({timeline?.labs?.length || 0})</TabsTrigger>
          <TabsTrigger value="radiology">Radiology ({timeline?.radiology?.length || 0})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({timeline?.invoices?.length || 0})</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">Files ({timeline?.files?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="appointments">
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {timeline?.appointments?.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0">
                    <td className="py-3 px-5 mono text-xs">{new Date(a.date).toLocaleString()}</td>
                    <td className="py-3 px-5">{a.reason || "—"}</td>
                    <td className="py-3 px-5"><Badge variant="outline">{a.status}</Badge></td>
                  </tr>
                ))}
                {(!timeline?.appointments?.length) && <tr><td className="py-8 text-center text-muted-foreground">No appointments.</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="records">
          <Card><CardContent className="p-0">
            <div className="divide-y divide-border">
              {timeline?.records?.map((r) => (
                <div key={r.id} className="p-5">
                  <div className="flex justify-between text-xs text-muted-foreground mono">
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                    <span>{r.doctor_name}</span>
                  </div>
                  <div className="mt-2"><strong>Diagnosis:</strong> {r.diagnosis || "—"}</div>
                  {r.symptoms && <div className="text-sm text-muted-foreground mt-1"><strong>Symptoms:</strong> {r.symptoms}</div>}
                  {r.notes && <div className="text-sm mt-1">{r.notes}</div>}
                </div>
              ))}
              {(!timeline?.records?.length) && <div className="py-8 text-center text-muted-foreground text-sm">No medical records yet.</div>}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="prescriptions">
          <Card><CardContent className="p-0">
            <div className="divide-y divide-border">
              {timeline?.prescriptions?.map((rx) => (
                <div key={rx.id} className="p-5">
                  <div className="flex justify-between text-xs text-muted-foreground mono">
                    <span>{new Date(rx.created_at).toLocaleString()}</span>
                    <span>{rx.doctor_name}</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {rx.items?.map((it, i) => (
                      <li key={`${rx.id}-${i}-${it.medicine}`} className="text-sm">• <strong>{it.medicine}</strong> — {it.dosage}, {it.duration}{it.instructions ? ` (${it.instructions})` : ""}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {(!timeline?.prescriptions?.length) && <div className="py-8 text-center text-muted-foreground text-sm">No prescriptions yet.</div>}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="labs">
          <Card><CardContent className="p-0">
            <div className="divide-y divide-border">
              {timeline?.labs?.map((l) => (
                <div key={l.id} className="p-5 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{l.tests?.join(", ")}</div>
                    <div className="text-xs text-muted-foreground mono mt-1">{new Date(l.created_at).toLocaleString()}</div>
                  </div>
                  <Badge variant="outline">{l.status}</Badge>
                </div>
              ))}
              {(!timeline?.labs?.length) && <div className="py-8 text-center text-muted-foreground text-sm">No lab requests.</div>}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="radiology">
          <Card><CardContent className="p-5 text-sm text-muted-foreground">
            {timeline?.radiology?.length ? timeline.radiology.map((r) => (
              <div key={r.id} className="py-2 border-b border-border last:border-0">
                <strong>{r.modality}</strong> — {r.body_part} ({r.status})
              </div>
            )) : "No radiology requests."}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {timeline?.invoices?.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-0">
                    <td className="py-3 px-5 mono text-xs">{i.invoice_number}</td>
                    <td className="py-3 px-5 mono">${i.total}</td>
                    <td className="py-3 px-5"><Badge variant="outline">{i.status}</Badge></td>
                  </tr>
                ))}
                {(!timeline?.invoices?.length) && <tr><td className="py-8 text-center text-muted-foreground text-sm">No invoices.</td></tr>}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="files">
          <Card><CardContent className="p-5">
            <FileUpload patientId={id} label="Patient files (all attachments)" />
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
